import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Clock,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Star,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { DevisForm } from "@/components/DevisForm";
import { PartnerSection } from "@/components/PartnerSection";
import { Marquee } from "@/components/Marquee";
import { Reveal } from "@/components/Reveal";
import { CONTACT } from "@/constants/contact";
import { IMAGES } from "@/constants/images";

const EASE = [0.22, 1, 0.36, 1];

const prestations = [
  {
    testid: "prestation-reparation-card",
    title: "Réparation mécanique",
    image: IMAGES.reparation,
    imageAlt: "Réparation mécanique assurée par MECA AUTO CLEAN 01 à Pont-d'Ain",
    intro:
      "Nous prenons en charge les réparations mécaniques de votre véhicule, quelle que soit la nature de la panne, avec un service sérieux et adapté à vos besoins.",
    items: [
      "Réparation moteur et périphériques",
      "Embrayage et boîte de vitesses",
      "Freinage et suspension",
      "Distribution et accessoires moteur",
      "Recherche et réparation de pannes mécaniques",
      "Remplacement de pièces défectueuses",
    ],
  },
  {
    testid: "prestation-entretien-card",
    title: "Entretien courant",
    image: IMAGES.entretien,
    imageAlt: "Entretien courant assuré par MECA AUTO CLEAN 01 à Pont-d'Ain",
    intro:
      "Un entretien régulier préserve les performances de votre véhicule et limite les risques de panne. Nous adaptons l'entretien à son kilométrage, son âge et les préconisations du constructeur.",
    items: [
      "Vidange moteur et remplacement des filtres",
      "Contrôle et remplacement des liquides",
      "Entretien du système de freinage",
      "Vérification des niveaux et éléments de sécurité",
      "Remplacement des pièces d'usure",
      "Préparation à un contrôle technique",
    ],
  },
  {
    testid: "prestation-diagnostic-card",
    title: "Diagnostic automobile",
    image: IMAGES.diagnostic,
    imageAlt: "Diagnostic automobile assuré par MECA AUTO CLEAN 01 à Pont-d'Ain",
    intro:
      "Un voyant allumé, une perte de puissance ou un fonctionnement anormal ? Nous recherchons l'origine du problème grâce à des outils de diagnostic adaptés.",
    items: [
      "Lecture des codes défauts",
      "Recherche de pannes électroniques",
      "Diagnostic moteur",
      "Analyse des voyants du tableau de bord",
      "Recherche de pertes de puissance",
      "Contrôles complémentaires avant réparation",
    ],
  },
];

const engagements = [
  {
    title: "Conseils transparents",
    text: "Nous vous expliquons simplement les travaux nécessaires, sans jargon inutile.",
  },
  {
    title: "Devis avant intervention",
    text: "Vous validez un devis clair avant que les réparations ne débutent.",
  },
  {
    title: "Pièces adaptées",
    text: "Nous privilégions des pièces et lubrifiants adaptés à votre véhicule.",
  },
  {
    title: "Un suivi dans la durée",
    text: "Un historique d'entretien pour anticiper les prochaines échéances.",
  },
];

const reviews = [
  {
    name: "Sascha Schmitz",
    date: "Visité en septembre — avis traduit par Google",
    testid: "review-sascha",
    text: "Notre voiture est tombée en panne samedi, et malgré le week-end, ils se sont démenés pour la réparer jusqu'à 18h afin que nous puissions reprendre la route. Nous leur sommes extrêmement reconnaissants et recommandons vivement ce garage et son personnel accueillant. Merci encore pour votre intervention rapide et compétente.",
  },
  {
    name: "Sabrina Atlani",
    date: "Visité en décembre 2025",
    testid: "review-sabrina",
    text: "Ça vaut même plus que 5 étoiles. Alors que je suis tombée en panne, aucun garage que j'ai appelé ne pouvait prendre en charge ma voiture. Puis vient la proposition tombée du ciel, après une prise en charge immédiate et un travail soigné. Un immense merci pour votre réactivité et votre gentillesse.",
  },
  {
    name: "Emilienne Chevillard",
    date: "Visité en septembre 2025",
    testid: "review-emilienne",
    text: "Sérieux et à l'écoute, pratique des tarifs raisonnables, travail rapide et soigné, je recommande.",
  },
];

const heroLines = ["Votre garage", "automobile", "à Pont-d'Ain"];

const Stars = () => (
  <div className="flex gap-0.5" aria-label="5 étoiles sur 5">
    {[...Array(5)].map((_, i) => (
      <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
    ))}
  </div>
);

export default function HomePage() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <div>
      {/* Hero */}
      <section ref={heroRef} data-testid="hero-section" className="relative overflow-hidden">
        <motion.img
          src={IMAGES.hero}
          alt="Voiture sportive noire aux feux rouges dans un garage sombre"
          data-testid="hero-bg-image"
          style={{ y: bgY, scale: bgScale }}
          className="absolute inset-0 h-[115%] w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0b0d10] via-[#0b0d10]/85 to-[#0b0d10]/25" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0b0d10] via-transparent to-[#0b0d10]/70" />
        <div className="hero-beam pointer-events-none absolute right-0 top-1/3 h-32 w-[34rem] bg-gradient-to-r from-transparent via-primary/25 to-transparent blur-2xl" />
        <div className="relative mx-auto max-w-6xl px-4 py-28 sm:py-36">
          <div className="max-w-2xl">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="mb-4 text-sm font-semibold uppercase tracking-[0.25em] text-primary drop-shadow-[0_0_12px_rgba(230,51,41,0.5)]"
            >
              Garage automobile à Pont-d'Ain
            </motion.p>
            <h1 className="font-display text-4xl font-bold uppercase leading-tight sm:text-5xl lg:text-6xl">
              {heroLines.map((line, i) => (
                <span key={line} className="block overflow-hidden pb-1">
                  <motion.span
                    className="block"
                    initial={{ y: "115%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: EASE }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.65, ease: EASE }}
              className="mt-6 text-base text-muted-foreground md:text-lg"
            >
              Chez <strong className="text-foreground">MECA AUTO CLEAN 01</strong>,
              nous prenons en charge les réparations mécaniques de votre
              véhicule, quelle que soit la nature de la panne. Notre objectif :
              vous permettre de reprendre la route en toute sérénité.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.8, ease: EASE }}
              className="mt-8 flex flex-wrap gap-3"
            >
              <Button asChild size="lg" data-testid="hero-devis-button" className="drop-shadow-[0_0_15px_rgba(230,51,41,0.4)]">
                <Link to="/#contact">Demander un devis</Link>
              </Button>
              <Button asChild size="lg" variant="outline" data-testid="hero-prestations-button" className="bg-background/40 backdrop-blur-sm">
                <Link to="/#prestations">Voir nos prestations</Link>
              </Button>
              <Button asChild size="lg" variant="ghost" data-testid="hero-call-button">
                <a href={CONTACT.phoneHref}>
                  <Phone className="mr-2 h-4 w-4" /> {CONTACT.phoneDisplay}
                </a>
              </Button>
            </motion.div>
          </div>
        </div>
        <motion.div
          data-testid="hero-scroll-hint"
          animate={{ y: [0, 10, 0], opacity: [0.35, 1, 0.35] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="absolute bottom-6 left-1/2 h-10 w-px -translate-x-1/2 bg-gradient-to-b from-primary to-transparent"
        />
      </section>

      <Marquee />

      {/* Prestations */}
      <section id="prestations" data-testid="prestations-section" className="scroll-mt-24">
        <div className="mx-auto max-w-6xl px-4 py-20">
          <Reveal>
            <p className="mb-2 text-sm font-semibold uppercase tracking-[0.25em] text-primary">
              Nos prestations
            </p>
            <h2 className="font-display text-3xl font-bold uppercase sm:text-4xl">
              Réparation, entretien et diagnostic de votre véhicule
            </h2>
            <p className="mt-4 max-w-2xl text-muted-foreground">
              De la panne mécanique à l'entretien de routine, MECA AUTO CLEAN 01
              vous accompagne pour garder votre voiture fiable et sûre, saison
              après saison.
            </p>
          </Reveal>

          <div className="mt-16 space-y-16">
            {prestations.map((p, i) => (
              <article
                key={p.title}
                data-testid={p.testid}
                className="grid items-center gap-8 lg:grid-cols-2"
              >
                <Reveal className={i % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="group overflow-hidden rounded-lg border border-border">
                    <img
                      src={p.image}
                      alt={p.imageAlt}
                      loading="lazy"
                      className="h-64 w-full object-cover brightness-90 contrast-105 transition-transform duration-700 group-hover:scale-105 lg:h-80"
                    />
                  </div>
                </Reveal>
                <Reveal delay={0.12} className={i % 2 === 1 ? "lg:order-1" : ""}>
                  <h3 className="font-display text-2xl font-semibold uppercase">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-muted-foreground">{p.intro}</p>
                  <ul className="mt-6 grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
                    {p.items.map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <Button asChild className="mt-6" data-testid={`${p.testid}-cta`}>
                    <Link to="/#contact">Demander un devis</Link>
                  </Button>
                </Reveal>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Le garage */}
      <section id="garage" data-testid="garage-section" className="scroll-mt-24 border-t border-border bg-card/40">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 py-20 lg:grid-cols-2">
          <Reveal>
            <p className="mb-2 text-sm font-semibold uppercase tracking-[0.25em] text-primary">
              Le garage
            </p>
            <h2 className="font-display text-3xl font-bold uppercase sm:text-4xl">
              Un service sérieux, adapté à vos besoins
            </h2>
            <p className="mt-6 text-muted-foreground">
              Chez MECA AUTO CLEAN 01, nous prenons en charge l'entretien et la
              réparation mécanique de votre véhicule à Pont-d'Ain. Nous vous
              proposons un accompagnement clair et de proximité, afin de vous
              permettre de reprendre la route en toute sérénité.
            </p>
            <div className="mt-8 space-y-5">
              <div className="flex items-start gap-4">
                <span className="font-display text-2xl font-bold text-primary">01</span>
                <p className="text-sm text-muted-foreground">
                  Des diagnostics précis pour identifier l'origine réelle de la
                  panne.
                </p>
              </div>
              <div className="flex items-start gap-4">
                <span className="font-display text-2xl font-bold text-primary">02</span>
                <p className="text-sm text-muted-foreground">
                  Un devis clair et transparent avant chaque intervention.
                </p>
              </div>
            </div>
            <ul className="mt-8 space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <span data-testid="garage-address">{CONTACT.address}</span>
              </li>
              <li>
                <a href={CONTACT.phoneHref} data-testid="garage-phone-link" className="flex items-center gap-3 hover:text-primary">
                  <Phone className="h-5 w-5 shrink-0 text-primary" /> {CONTACT.phoneDisplay}
                </a>
              </li>
              <li>
                <a href={`mailto:${CONTACT.email}`} data-testid="garage-email-link" className="flex items-center gap-3 hover:text-primary">
                  <Mail className="h-5 w-5 shrink-0 text-primary" /> {CONTACT.email}
                </a>
              </li>
            </ul>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="group overflow-hidden rounded-lg border border-border">
              <img
                src={IMAGES.garage}
                alt="Mécanicien de MECA AUTO CLEAN 01 effectuant une réparation sous un véhicule"
                data-testid="garage-image"
                loading="lazy"
                className="h-full max-h-[480px] w-full object-cover brightness-90 contrast-105 transition-transform duration-700 group-hover:scale-105"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Engagements */}
      <section id="engagements" data-testid="engagements-section" className="scroll-mt-24 border-t border-border">
        <div className="mx-auto max-w-6xl px-4 py-20">
          <Reveal>
            <p className="mb-2 text-sm font-semibold uppercase tracking-[0.25em] text-primary">
              Nos engagements
            </p>
            <h2 className="font-display text-3xl font-bold uppercase sm:text-4xl">
              Une prise en charge claire, du diagnostic à la réparation
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {engagements.map((e, i) => (
              <Reveal key={e.title} delay={i * 0.08}>
                <div
                  data-testid={`engagement-card-${i + 1}`}
                  className="h-full rounded-lg border border-border bg-card p-6 transition-colors card-lift hover:border-primary/50"
                >
                  <span className="font-display text-2xl font-bold text-primary/40">
                    0{i + 1}
                  </span>
                  <h3 className="mt-3 font-semibold">{e.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{e.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="group mt-10 overflow-hidden rounded-lg border border-border">
              <img
                src={IMAGES.atelier}
                alt="Atelier de réparation automobile équipé de MECA AUTO CLEAN 01"
                data-testid="atelier-image"
                loading="lazy"
                className="h-56 w-full object-cover brightness-90 contrast-105 transition-transform duration-700 group-hover:scale-105 sm:h-72"
              />
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8">
              <Button asChild size="lg" data-testid="engagements-rdv-button">
                <Link to="/#contact">Prendre rendez-vous</Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Avis clients */}
      <section id="avis" data-testid="reviews-section" className="scroll-mt-24 border-t border-border bg-card/40">
        <div className="mx-auto max-w-6xl px-4 py-20">
          <Reveal>
            <p className="mb-2 text-sm font-semibold uppercase tracking-[0.25em] text-primary">
              Avis clients
            </p>
            <h2 className="font-display text-3xl font-bold uppercase sm:text-4xl">
              Ce que disent nos clients
            </h2>
            <p className="mt-4 max-w-2xl text-muted-foreground">
              Des avis vérifiés de clients venus pour une réparation, un entretien
              ou une prise en charge d'urgence à Pont-d'Ain.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {reviews.map((r, i) => (
              <Reveal key={r.name} delay={i * 0.08}>
                <figure
                  data-testid={r.testid}
                  className="flex h-full flex-col rounded-lg border border-border bg-card p-6 transition-colors card-lift hover:border-primary/50"
                >
                  <Stars />
                  <blockquote className="mt-4 flex-1 text-sm text-muted-foreground">
                    « {r.text} »
                  </blockquote>
                  <figcaption className="mt-4 text-sm">
                    <span className="font-semibold">{r.name}</span>
                    <span className="block text-xs text-muted-foreground">
                      {r.date} — Avis Google
                    </span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center">
              <p className="text-sm text-muted-foreground">
                Vous êtes déjà passé par le garage ? Votre retour nous aide à
                progresser et rassure les prochains clients.
              </p>
              <Button asChild variant="outline" data-testid="google-review-button" className="shrink-0">
                <a href={CONTACT.googleReviewLink} target="_blank" rel="noopener noreferrer">
                  <Star className="mr-2 h-4 w-4" /> Laisser un avis sur Google
                </a>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>

      <PartnerSection />

      {/* Contact */}
      <section id="contact" data-testid="contact-section" className="brand-panel scroll-mt-24 border-t border-border">
        <div className="mx-auto grid max-w-6xl gap-10 px-4 py-20 lg:grid-cols-2">
          <Reveal>
            <p className="mb-2 text-sm font-semibold uppercase tracking-[0.25em] text-primary">
              Devis / Contact
            </p>
            <h2 className="font-display text-3xl font-bold uppercase sm:text-4xl">
              Besoin d'une réparation ?
            </h2>
            <p className="mt-4 text-muted-foreground">
              Contactez MECA AUTO CLEAN 01 pour obtenir des renseignements ou
              demander un devis. Décrivez votre panne ou votre besoin
              d'entretien : votre demande nous parvient directement par WhatsApp
              et nous vous répondons sur le numéro que vous indiquez. Véhicule
              de prêt ou location possible sur demande.
            </p>
            <ul className="mt-8 space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <span>{CONTACT.address}</span>
              </li>
              <li>
                <a href={CONTACT.phoneHref} data-testid="contact-phone-link" className="flex items-center gap-3 hover:text-primary">
                  <Phone className="h-5 w-5 shrink-0 text-primary" /> {CONTACT.phoneDisplay}
                </a>
              </li>
              <li>
                <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="flex items-center gap-3 hover:text-primary">
                  <Mail className="h-5 w-5 shrink-0 text-primary" /> {CONTACT.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                <span data-testid="contact-hours">
                  {CONTACT.hours.map((h) => (
                    <span key={h} className="block">{h}</span>
                  ))}
                </span>
              </li>
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild data-testid="contact-whatsapp-button" className="bg-[#25d366] text-white hover:bg-[#1eb857]">
                <a href={CONTACT.whatsappLink} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="mr-2 h-4 w-4" /> Écrire sur WhatsApp
                </a>
              </Button>
              <Button asChild variant="outline" data-testid="contact-call-button">
                <a href={CONTACT.phoneHref}>
                  <Phone className="mr-2 h-4 w-4" /> Appeler le garage
                </a>
              </Button>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-lg border border-border bg-card p-6">
              <p className="font-display text-lg font-semibold uppercase">Devis gratuit</p>
              <p className="mb-6 mt-1 text-sm text-muted-foreground">
                Remplissez ce formulaire, nous vous répondons directement sur
                votre téléphone.
              </p>
              <DevisForm />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
